/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import can from 'can';
import _ from 'lodash';
import template from './comparison-summary.stache';
import { PROBABILITY_PAGE_TYPE } from 'components/probability/tooltip-handler';
import { prepareChartData } from 'components/probability/computes';
import Swiper from 'swiper';
import {
    hideTooltip,
    parseTooltipId,
} from 'components/probability/tooltipHelpers';
import { COMPARISON_SUMMARY_TABS } from 'components/player/player-comparison/comparison-summary/comparison-summary-tabs/comparison-summary-tabs';
import {
    MAKE_CUT_PROBABILITY,
    TOP_10_PROBABILITY,
    WIN_PROBABILITY,
} from 'components/probability/types';
import {
    getPlayerName,
    sendChartAnalytics,
    sendTabChangeAnalytics,
} from 'components/probability/controllers/analytics.controller';

function mapProbabilityType(type) {
    switch (type) {
        case WIN_PROBABILITY: {
            return 'winProbabilityRounds';
        }
        case MAKE_CUT_PROBABILITY: {
            return 'makeCutRounds';
        }
        case TOP_10_PROBABILITY: {
            return 'top10Rounds';
        }
        default: {
            return '';
        }
    }
}

function getCompareNames(firstPlayer, secondPlayer, index) {
    const firstPlayerName = getPlayerName(firstPlayer);
    const secondPlayerName = getPlayerName(secondPlayer);

    return `${firstPlayerName}, ${secondPlayerName}, ${
        [firstPlayerName, secondPlayerName][index]
    }`;
}

can.Component.extend({
    tag: 'comparison-summary',
    template: template,
    leakScope: false,
    viewModel: {
        define: {
            pageType: {
                type: 'string',
                value: PROBABILITY_PAGE_TYPE.PLAYER_COMPARISON,
            },
            firstPlayerInfo: {
                Type: can.Map,
                Value: can.Map,
            },
            secondPlayerInfo: {
                Type: can.Map,
                Value: can.Map,
            },
            showVideo: {
                type: 'boolean',
                value: false,
            },
            chartsData: {
                type: 'array',
                value: [],
            },
            yAxis: {
                type: 'array',
                value: [],
            },
            firstPlayerProbability: {
                type: 'object',
            },
            secondPlayerProbability: {
                type: 'object',
            },
            isSwiperBeginning: {
                type: 'boolean',
                value: true,
            },
            isSwiperEnd: {
                type: 'boolean',
                value: false,
            },
            activeTooltipId: {
                type: 'string',
            },
            probabilityType: {
                type: 'string',
                value: COMPARISON_SUMMARY_TABS[0].value,
            },
        },
        getTooltipPlayers({ round, hole, probability }) {
            const firstPlayerProbability = this.attr(
                `chartsData.${round - 1}.points.playerPoints.${hole}`,
            );
            const secondPlayerProbability = this.attr(
                `chartsData.${round - 1}.points.secondPlayerPoints.${hole}`,
            );

            return [firstPlayerProbability, secondPlayerProbability].findIndex(
                (it) => it === probability,
            );
        },
        hideTooltip() {
            const tooltip = this.attr('tooltip');
            hideTooltip(tooltip);
        },
        onTabChange(_, _2, { tab }) {
            this.attr('probabilityType', tab.value);
            sendTabChangeAnalytics(
                tab.value,
                null,
                this.attr('pageType'),
                `${getPlayerName(
                    this.attr('firstPlayerInfo.player'),
                )}, ${getPlayerName(this.attr('secondPlayerInfo.player'))}`,
            );
        },
    },
    events: {
        inserted() {
            this.viewModel.attr('tooltip', this.element.find('.c-tooltip'));
            this.initSwiper();
            const playersDataCompute = can.compute(() => ({
                firstPlayerInfo: this.viewModel.attr('firstPlayerInfo'),
                secondPlayerInfo: this.viewModel.attr('secondPlayerInfo'),
                probabilityType: this.viewModel.attr('probabilityType'),
            }));

            playersDataCompute.bind('change', (_, value) => {
                this.prepareChartData(value);
            });

            this.viewModel.bind('chartsData', () => {
                this.updateSwiper();
            });
        },
        initSwiper() {
            if (this.swiper) {
                this.swiper.destroy(true);
                this.swiper = null;
            }

            const $swiperContainer = this.element.find('.swiper-container');
            const isContainerExists = Boolean($swiperContainer.length);

            if (!isContainerExists) {
                return;
            }

            this.swiper = new Swiper($swiperContainer, {
                simulateTouch: true,
                slidesPerView: 'auto',
                freeMode: true,
                observer: true,
                observeSlideChildren: true,
                scrollbar: {
                    el: this.element.find('.swiper-scrollbar'),
                    dragSize: 28,
                    draggable: true,
                    snapOnRelease: false,
                },
                on: {
                    progress: (progress) => {
                        this.viewModel.attr('isSwiperEnd', progress >= 1);
                        this.viewModel.attr('isSwiperBeginning', progress <= 0);
                        this.viewModel.hideTooltip();
                    },
                    observerUpdate: () => this.updateSwiper(),
                },
            });
        },

        updateSwiper() {
            if (!this.swiper) {
                return;
            }

            this.swiper.update();
            this.swiper.scrollbar.updateSize();
        },
        processChartData(firstPlayerRounds, secondPlayerRounds, preparedData) {
            if (
                _.isEqual(
                    this.viewModel.attr('chartsData').serialize(),
                    preparedData,
                )
            ) {
                return;
            }

            this.viewModel.attr('firstPlayerProbability', firstPlayerRounds);
            this.viewModel.attr('secondPlayerProbability', secondPlayerRounds);
            this.viewModel.attr('chartsData', preparedData);
            this.viewModel.attr('yAxis', preparedData[0].yAxis);
        },
        prepareChartData({
            firstPlayerInfo,
            secondPlayerInfo,
            probabilityType,
        }) {
            const probabilityPath = `probability.${mapProbabilityType(
                probabilityType,
            )}`;

            const firstPlayerRounds = firstPlayerInfo.attr(probabilityPath);
            const secondPlayerRounds = secondPlayerInfo.attr(probabilityPath);

            if (!firstPlayerRounds || !secondPlayerRounds) {
                return;
            }

            const preparedData = prepareChartData(
                firstPlayerRounds.serialize(),
                secondPlayerRounds.serialize(),
            );

            this.processChartData(
                firstPlayerRounds,
                secondPlayerRounds,
                preparedData,
            );
        },
        '.s-graph-dot mouseover'() {
            const value = this.viewModel.attr('activeTooltipId');

            const params = parseTooltipId(value);

            const index = this.viewModel.getTooltipPlayers(params);

            const firstPlayer = this.viewModel.attr('firstPlayerInfo.player');
            const secondPlayer = this.viewModel.attr('secondPlayerInfo.player');

            const player = [firstPlayer, secondPlayer][index];
            const compareName = getCompareNames(
                firstPlayer,
                secondPlayer,
                index,
            );

            sendChartAnalytics(
                {
                    type: this.viewModel.attr('probabilityType'),
                    player,
                    pageType: this.viewModel.attr('pageType'),
                    compareName,
                },
                value,
            );
        },
    },
});
